import axios from '@axios'

function getApiOrigin() {
  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : ''
  const baseURL = axios.defaults?.baseURL || browserOrigin

  try {
    return new URL(baseURL).origin
  } catch (e) {
    return browserOrigin
  }
}

function normalizeAssetUrl(url) {
  if (!url || typeof url !== 'string') return url

  const trimmedUrl = url.trim()
  if (!trimmedUrl) return ''

  if (/^(?:[a-z]+:)?\/\//i.test(trimmedUrl) || trimmedUrl.startsWith('data:') || trimmedUrl.startsWith('blob:')) {
    return trimmedUrl
  }

  const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`
  const apiOrigin = getApiOrigin()

  return apiOrigin ? `${apiOrigin}${normalizedPath}` : normalizedPath
}

function normalizeLojaAssets(loja) {
  if (!loja || typeof loja !== 'object') return loja

  return {
    ...loja,
    logo_header: normalizeAssetUrl(loja.logo_header),
    logo_footer: normalizeAssetUrl(loja.logo_footer),
    favicon: normalizeAssetUrl(loja.favicon),
  }
}

function normalizeBannerAssets(banner) {
  if (!banner || typeof banner !== 'object') return banner

  return {
    ...banner,
    imagem: normalizeAssetUrl(banner.imagem),
    imagem_mobile: normalizeAssetUrl(banner.imagem_mobile),
  }
}

export default {
  namespaced: true,
  state: {
    loja: {},
    banners: [],
    categorias: [],
    allCategorias: [],
    loading: false,
  },
  mutations: {
    SET_LOJA(state, loja) {
      state.loja = loja
    },
    SET_BANNERS(state, banners) {
      state.banners = banners
    },
    SET_CATEGORIAS(state, categorias) {
      state.categorias = categorias
    },
    SET_ALL_CATEGORIAS(state, categorias) {
      state.allCategorias = categorias
    },
    SET_LOADING(state, val) {
      state.loading = val
    },
  },
  actions: {
    async fetchLoja({ commit }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.get('/vendedor-loja/minha-loja')
        const normalizedData = normalizeLojaAssets(data)
        commit('SET_LOJA', normalizedData)
        return normalizedData
      } catch (e) {
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async saveLoja({ commit }, payload) {
      const { data } = await axios.post('/vendedor-loja/minha-loja', payload)
      const normalizedData = normalizeLojaAssets(data)
      commit('SET_LOJA', normalizedData)
      return normalizedData
    },
    async checkSlug(_, slug) {
      const { data } = await axios.get(`/vendedor-loja/check-slug/${slug}`)
      return data
    },
    async fetchBanners({ commit }) {
      const { data } = await axios.get('/vendedor-loja/banners')
      const normalizedData = Array.isArray(data) ? data.map(normalizeBannerAssets) : []
      commit('SET_BANNERS', normalizedData)
      return normalizedData
    },
    async createBanner({ dispatch }, payload) {
      const { data } = await axios.post('/vendedor-loja/banners', payload)
      await dispatch('fetchBanners')
      return data
    },
    async updateBanner({ dispatch }, { id, payload }) {
      const { data } = await axios.put(`/vendedor-loja/banners/${id}`, payload)
      await dispatch('fetchBanners')
      return data
    },
    async deleteBanner({ dispatch }, id) {
      await axios.delete(`/vendedor-loja/banners/${id}`)
      await dispatch('fetchBanners')
    },
    async fetchCategorias({ commit }) {
      const { data } = await axios.get('/vendedor-loja/categorias')
      commit('SET_CATEGORIAS', data)
      return data
    },
    async fetchAllCategorias({ commit }) {
      const { data } = await axios.get('/categorias')
      const list = data.categories || data.rows || data
      commit('SET_ALL_CATEGORIAS', Array.isArray(list) ? list : [])
      return Array.isArray(list) ? list : []
    },
    async syncCategorias({ dispatch }, categorias) {
      const { data } = await axios.post('/vendedor-loja/categorias', { categorias })
      await dispatch('fetchCategorias')
      return data
    },
    async uploadFile(_, file) {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await axios.post('/vendedor-loja/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return normalizeAssetUrl(data.url || data.image_url || data.location)
    },
  },
}
